// =====================================================================================
// INVRT Radar - web console - Pagination (canonical mirror, Story N3)
//
// Mirror of the INVRT Design System's Primitives/Pagination for a cursor-paginated seam:
// the canonical component renders a page-number row from `total`, but an opaque-cursor API
// publishes no total, and inventing one would lie. This renders the honest subset -
// Previous / page readout / Next, with the readout announced politely - at the same 44px
// target size and with the same visible focus treatment as the rest of the console.
// =====================================================================================

import { cn } from "@/lib/utils";
import { Button } from "./button";

export interface CursorPaginationProps {
  /** Zero-based page index, shown to people as "Page N". */
  page: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  /** Accessible name when more than one pager ever shares a screen. */
  label?: string;
  className?: string;
}

export function Pagination({
  page,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  label = "Leads pagination",
  className,
}: CursorPaginationProps) {
  return (
    <nav aria-label={label} className={cn("flex flex-wrap items-center gap-3", className)}>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="min-h-11 min-w-11"
        onClick={onPrevious}
        disabled={!hasPrevious}
        aria-label="Previous page"
      >
        Previous
      </Button>
      <span className="text-sm text-ink-muted" aria-live="polite">
        Page {page + 1}
      </span>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="min-h-11 min-w-11"
        onClick={onNext}
        disabled={!hasNext}
        aria-label="Next page"
      >
        Next
      </Button>
    </nav>
  );
}
